import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * TMC free tee (DECISIONS #115).
 * 1. <tmc-gift-sync> (rendered by snippets/tmc-cart-gift.liquid in the cart drawer and cart page) keeps the
 *    tee line right: adds it once when a gift product (e.g. Avengers: Doomsday) is in the cart, removes it when
 *    none is left, and keeps it at quantity 1. A Shopify automatic discount makes that one tee ₹0.
 *    If the shopper removes the tee themselves, it is not forced back: the row shows "Add my free tee" instead.
 * 2. [data-tmc-gift-add] buttons add the tee on demand.
 * 3. [data-tmc-gift-open] buttons open the tee pop-up (<dialog id="TmcGiftDialog">, snippets/tmc-gift-dialog).
 * Cart changes go through Shopify's AJAX cart and announce Horizon's CartLinesUpdateEvent with fresh sections,
 * so the drawer, cart page and cart count redraw exactly as for Horizon's own buttons.
 */

const AUTO_KEY = 'tmcTeeAutoAdded';
let busy = false;

/** @returns {string[]} section ids of the cart-items components on the page */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean);
}

/**
 * @param {'add' | 'change'} kind
 * @param {Record<string, any>} payload
 * @param {HTMLElement} source - element the event is dispatched from
 */
async function cartRequest(kind, payload, source) {
  if (busy) return;
  busy = true;
  const sections = cartSectionIds();
  const body = JSON.stringify({ ...payload, sections: sections.join(',') });
  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(
    new CartLinesUpdateEvent({
      action: kind === 'add' ? 'add' : 'update',
      context: 'cart',
      lines: [{ merchandiseId: String(payload.id), quantity: payload.quantity }],
      promise: deferred.promise,
    })
  );
  try {
    const url = kind === 'add' ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
    const response = await fetch(url, fetchConfig('json', { body }));
    const result = await response.json();
    if (result.status || result.errors) throw new Error(result.description || result.message || 'Cart update failed');
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections: result.sections, source: 'tmc-gift', didError: false },
    });
  } catch (error) {
    console.warn('[tmc-gift]', error);
    deferred.reject(error);
  } finally {
    busy = false;
  }
}

function storage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

class TmcGiftSync extends HTMLElement {
  static observedAttributes = ['data-needed', 'data-qty', 'data-key'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#schedule();
  }

  // Horizon morphs the cart section in place, so this element usually survives a cart change and only its
  // attributes update: react to those too.
  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#sync(), 150);
  }

  #sync() {
    const needed = this.dataset.needed === 'true';
    const qty = Number(this.dataset.qty) || 0;
    const variant = this.dataset.variant;
    const key = this.dataset.key;
    const store = storage();
    if (!variant) return;

    if (!needed && qty > 0 && key) {
      cartRequest('change', { id: key, quantity: 0 }, this);
      return;
    }
    if (needed && qty > 1 && key) {
      cartRequest('change', { id: key, quantity: 1 }, this);
      return;
    }
    if (needed && qty === 0 && store?.getItem(AUTO_KEY) !== '1') {
      store?.setItem(AUTO_KEY, '1');
      cartRequest('add', { id: Number(variant), quantity: 1 }, this);
    }
    if (needed && qty === 1) store?.setItem(AUTO_KEY, '1');
  }
}

if (!customElements.get('tmc-gift-sync')) customElements.define('tmc-gift-sync', TmcGiftSync);

if (!(/** @type {any} */ (window).__tmcGift)) {
  /** @type {any} */ (window).__tmcGift = true;
  document.addEventListener('click', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const add = target.closest('[data-tmc-gift-add]');
    if (add) {
      event.preventDefault();
      const sync = /** @type {HTMLElement | null} */ (document.querySelector('tmc-gift-sync'));
      if (sync?.dataset.variant) cartRequest('add', { id: Number(sync.dataset.variant), quantity: 1 }, sync);
      return;
    }
    const open = target.closest('[data-tmc-gift-open]');
    if (open) {
      const dialog = /** @type {HTMLDialogElement | null} */ (document.getElementById('TmcGiftDialog'));
      if (!dialog) return;
      event.preventDefault();
      event.stopPropagation();
      dialog.showModal();
      return;
    }
    if (target.closest('[data-tmc-gift-close]') || (target instanceof HTMLDialogElement && target.id === 'TmcGiftDialog')) {
      /** @type {HTMLDialogElement | null} */ (document.getElementById('TmcGiftDialog'))?.close();
    }
  });
}
